import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './PayRentPage.css';

// Standalone, public rent bill: a tenant opens it from a link the owner shares
// (WhatsApp/SMS/reminder email), no account or login required. Rendered
// outside the normal app shell (see App.js).
//
// Money goes straight to the owner (their UPI ID, payment link or bank
// account), or online through Spaczo's gateway when that is switched on.
// Guarding against paying twice:
//   • once a bill is paid, or the tenant said "I've paid", no Pay buttons show
//   • tapping Pay is remembered, so coming back after a dropped network or a
//     closed app shows "check your UPI app before paying again"
//   • "I've paid" is safe to retry; the same reference can't be used twice
//   • an earlier online payment is checked before another one can start

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmtTime = (d) => new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
const fmtDate = (d) => (d ? new Date(d.length === 10 ? `${d}T12:00:00` : d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const startedKey = (id) => `spaczo_paystart_${id}`;
const isMobile = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

function upiLink(m, rent) {
  const params = new URLSearchParams({ pa: m.upiId, pn: m.upiName || '', am: String(Number(rent.amount).toFixed(2)), cu: 'INR', tn: `Rent ${rent.periodLabel || rent.period}`.slice(0, 50) });
  return `upi://pay?${params.toString()}`;
}

function Copy({ text, label }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 2000); } catch {}
  };
  return <button type="button" className="pr-copy" onClick={copy} aria-label={`Copy ${label}`}>{done ? 'Copied ✓' : 'Copy'}</button>;
}

function UpiQr({ value }) {
  const [src, setSrc] = useState('');
  useEffect(() => {
    let alive = true;
    import('qrcode').then(QR => QR.toDataURL(value, { margin: 1, width: 220 })).then(u => { if (alive) setSrc(u); }).catch(() => {});
    return () => { alive = false; };
  }, [value]);
  return src ? <img className="pr-qr" src={src} alt="UPI QR code for this rent" width="220" height="220" /> : null;
}

export default function PayRentPage({ rentId }) {
  const [loading, setLoading] = useState(true);
  const [rent, setRent] = useState(null);
  const [error, setError] = useState('');
  const [paying, setPaying] = useState(false);
  const [claim, setClaim] = useState({ method: 'upi', utr: '', note: '' });
  const [claiming, setClaiming] = useState(false);
  const [claimErr, setClaimErr] = useState('');
  const [localStart, setLocalStart] = useState(() => {
    try { return Number(localStorage.getItem(startedKey(rentId))) || 0; } catch { return 0; }
  });

  const load = useCallback(() => axios.get(`/api/rent-payments/public/${rentId}`)
    .then(res => { setRent(res.data.data); setError(''); })
    .catch(() => setError('This rent link is invalid or has expired.'))
    .finally(() => setLoading(false)), [rentId]);
  useEffect(() => { load(); }, [load]);

  // "You started a payment …" (from this phone, or from any device via the server)
  const serverStart = rent?.payStartedAt ? Date.parse(rent.payStartedAt) : 0;
  const started = Math.max(localStart, serverStart);
  const startedRecently = started && Date.now() - started < 3 * 86400e3;

  const markStarted = (method) => {
    const now = Date.now();
    try { localStorage.setItem(startedKey(rentId), String(now)); } catch {}
    setLocalStart(now);
    setClaim(c => ({ ...c, method }));
    axios.post(`/api/rent-payments/public/${rentId}/started`).catch(() => {});
  };

  const submitClaim = async (e) => {
    e.preventDefault();
    setClaimErr('');
    if (claim.method !== 'cash' && !/^[A-Za-z0-9]{6,30}$/.test(claim.utr.replace(/\s/g, ''))) {
      setClaimErr('Enter the transaction / UTR number from your UPI or bank app.');
      return;
    }
    setClaiming(true);
    try {
      await axios.post(`/api/rent-payments/public/${rentId}/claim`, { ...claim, utr: claim.utr.replace(/\s/g, '') });
      await load();
    } catch (err) {
      if (!err.response) {
        // Network dropped: the claim may or may not have reached us. Check the
        // bill instead of asking them to send it again blindly.
        await load();
        setClaimErr('Connection problem. We re-checked your bill above. If it still shows unpaid, tap “I’ve paid” again: it won’t be recorded twice.');
      } else {
        setClaimErr(err.response?.data?.message || 'Could not send. Please try again.');
      }
    } finally { setClaiming(false); }
  };

  const payOnline = async () => {
    setPaying(true); setError('');
    try {
      markStarted('link');
      const { data: order } = await axios.post(`/api/rent-payments/public/${rentId}/create-order`);
      const { load: loadCashfree } = await import('@cashfreepayments/cashfree-js');
      const cashfree = await loadCashfree({ mode: order.env === 'production' ? 'production' : 'sandbox' });
      const result = await cashfree.checkout({ paymentSessionId: order.paymentSessionId, redirectTarget: '_modal' });
      if (result?.error) { setError('Payment was not completed. You can try again.'); return; }
      await axios.post(`/api/rent-payments/public/${rentId}/verify`, { orderId: order.orderId }).catch(() => {});
    } catch (err) {
      setError(err.response?.data?.message || 'Payment failed. If money was deducted, don’t pay again: reopen this page in a few minutes.');
    } finally {
      setPaying(false);
      load(); // always re-check: the payment may have gone through even if this page lost track
    }
  };

  if (loading) {
    return <div className="pay-rent-page"><div className="pay-rent-card"><div className="spinner" style={{ width: 32, height: 32, borderColor: '#eee', borderTopColor: '#E53935', margin: '40px auto' }} /></div></div>;
  }
  if (error && !rent) {
    return <div className="pay-rent-page"><div className="pay-rent-card"><div className="pay-rent-icon">⚠️</div><h2>Link not valid</h2><p className="pay-rent-error">{error}</p></div></div>;
  }

  const head = (
    <>
      <div className="pay-rent-logo">📍 Spaczo</div>
      <div className="pay-rent-details">
        <div className="pay-rent-row"><span>Property</span><strong>{rent.propertyTitle}</strong></div>
        {rent.propertyLocation && <div className="pay-rent-row"><span>Location</span><strong>{rent.propertyLocation}</strong></div>}
        {rent.tenantName && <div className="pay-rent-row"><span>Tenant</span><strong>{rent.tenantName}</strong></div>}
        <div className="pay-rent-row"><span>Rent for</span><strong>{rent.periodLabel || rent.period}</strong></div>
        {rent.dueDate && <div className="pay-rent-row"><span>Due</span><strong>{fmtDate(rent.dueDate)}</strong></div>}
      </div>
      <div className="pay-rent-amount">{inr(rent.amount)}</div>
    </>
  );

  if (rent.status === 'paid') {
    return (
      <div className="pay-rent-page"><div className="pay-rent-card">
        {head}
        <div className="pr-state good">✅ Paid{rent.paidAt ? ` · ${fmtDate(rent.paidAt)}` : ''}. Thank you!</div>
        <p className="pay-rent-note">Nothing more to pay for this month.</p>
      </div></div>
    );
  }
  if (rent.status === 'claimed') {
    return (
      <div className="pay-rent-page"><div className="pay-rent-card">
        {head}
        <div className="pr-state wait">🕓 You told {rent.ownerName} this is paid ({fmtTime(rent.claimedAt)}{rent.claimRef ? `, ref ${rent.claimRef}` : ''}). Waiting for them to confirm.</div>
        <p className="pay-rent-note"><strong>Don’t pay again.</strong> If your owner says it didn’t arrive, you’ll be told, and you can check your UPI app.</p>
      </div></div>
    );
  }

  const m = rent.methods;
  return (
    <div className="pay-rent-page">
      <div className="pay-rent-card pr-card">
        {head}

        {rent.ownerDisputedAt && (
          <div className="pr-state bad">⚠️ {rent.ownerName} hasn’t received your earlier payment. Check your UPI or bank app first: failed payments are usually refunded automatically. Don’t pay again until you’ve checked.</div>
        )}
        {startedRecently && !rent.ownerDisputedAt && (
          <div className="pr-state warn">
            ⏳ A payment for this bill was started on {fmtTime(started)}. <strong>Check your UPI app’s history first.</strong> If the money went, tap “I’ve paid” below instead of paying again.
          </div>
        )}

        {m ? (
          <div className="pr-methods">
            {m.recentlyChanged && (
              <div className="pr-state warn">The owner changed their payment details {m.changedDaysAgo === 0 ? 'today' : `${m.changedDaysAgo} day${m.changedDaysAgo === 1 ? '' : 's'} ago`}. If you weren’t expecting this, call your owner before paying.</div>
            )}
            {m.upiId && (
              <section className="pr-method">
                <h3>Pay by UPI</h3>
                {isMobile() ? (
                  <a className="pay-rent-btn pr-paybtn" href={upiLink(m, rent)} onClick={() => markStarted('upi')}>Pay {inr(rent.amount)} with a UPI app</a>
                ) : (
                  <>
                    <UpiQr value={upiLink(m, rent)} />
                    <p className="pay-rent-note">Scan with GPay, PhonePe, Paytm or any UPI app. The amount is filled in.</p>
                    <button type="button" className="pr-link" onClick={() => markStarted('upi')}>I’ve scanned the code and I’m paying now</button>
                  </>
                )}
                <div className="pr-field"><span>UPI ID</span><b>{m.upiId}</b><Copy text={m.upiId} label="UPI ID" /></div>
                <p className="pr-payee">You’re paying <b>{m.upiName}</b>. Check this name in your UPI app before you pay.</p>
              </section>
            )}
            {m.payLink && (
              <section className="pr-method">
                <h3>Pay with {rent.ownerName}’s payment link</h3>
                <a className="pay-rent-btn pr-paybtn secondary" href={m.payLink} target="_blank" rel="noopener noreferrer" onClick={() => markStarted('link')}>Open payment page ↗</a>
                <p className="pay-rent-note">Opens {new URL(m.payLink).hostname}. Enter {inr(rent.amount)} if it asks.</p>
              </section>
            )}
            {m.bank && (
              <section className="pr-method">
                <h3>Bank transfer (NEFT / IMPS)</h3>
                <div className="pr-field"><span>Name</span><b>{m.bank.accountName}</b></div>
                <div className="pr-field"><span>Account</span><b>{m.bank.accountNumber}</b><Copy text={m.bank.accountNumber} label="account number" /></div>
                <div className="pr-field"><span>IFSC</span><b>{m.bank.ifsc}</b><Copy text={m.bank.ifsc} label="IFSC" /></div>
                {m.bank.bankName && <div className="pr-field"><span>Bank</span><b>{m.bank.bankName}</b></div>}
              </section>
            )}
          </div>
        ) : !rent.onlinePayment && (
          <p className="pay-rent-note">Pay {rent.ownerName} the usual way (UPI, bank transfer or cash), then tell them below.</p>
        )}

        {rent.onlinePayment && (
          <section className="pr-method">
            <h3>Pay online (card, net banking, UPI)</h3>
            {error && <div className="pay-rent-error">{error}</div>}
            <button className="pay-rent-btn" onClick={payOnline} disabled={paying}>{paying ? 'Processing…' : `Pay ${inr(rent.amount)} online`}</button>
          </section>
        )}

        <form className="pr-claim" onSubmit={submitClaim}>
          <h3>Already paid? Tell {rent.ownerName}</h3>
          <div className="pr-claim-methods" role="radiogroup" aria-label="How you paid">
            {[['upi', 'UPI'], ['bank', 'Bank'], ['link', 'Payment link'], ['cash', 'Cash']].map(([k, l]) => (
              <button type="button" key={k} role="radio" aria-checked={claim.method === k} className={claim.method === k ? 'on' : ''} onClick={() => setClaim(c => ({ ...c, method: k }))}>{l}</button>
            ))}
          </div>
          {claim.method !== 'cash' && (
            <label className="pr-label">Transaction / UTR number
              <input className="pay-rent-input" inputMode="text" autoComplete="off" maxLength={30} value={claim.utr}
                onChange={e => setClaim(c => ({ ...c, utr: e.target.value.toUpperCase() }))} placeholder="e.g. 432198765432 (in your UPI app’s payment details)" />
            </label>
          )}
          <label className="pr-label">Note for {rent.ownerName} (optional)
            <input className="pay-rent-input" maxLength={200} value={claim.note} onChange={e => setClaim(c => ({ ...c, note: e.target.value }))} />
          </label>
          {claimErr && <div className="pay-rent-error">{claimErr}</div>}
          <button className="pay-rent-btn pr-claim-btn" disabled={claiming}>{claiming ? 'Sending…' : 'I’ve paid'}</button>
          <p className="pay-rent-note">Your owner checks their bank app and confirms. This link is only for the tenant it was shared with.</p>
        </form>
      </div>
    </div>
  );
}
